import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plane, Hotel, User, Calendar, Clock, MapPin,
  CreditCard, Shield, AlertTriangle, Check, X,
  Loader2, ChevronDown, ChevronUp, Info, Luggage,
  Coffee, Wifi, Car, Ban, RefreshCw, AlertCircle, Users
} from 'lucide-react';
import { toast } from 'sonner';
import HotelGuestForm from './HotelGuestForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Booking state machine to prevent accidental bookings
const BOOKING_STATES = {
  IDLE: 'idle',
  REVIEWING: 'reviewing',
  CONFIRMING: 'confirming',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error',
  VERIFYING: 'verifying'
};

// Idempotency key generator
const generateIdempotencyKey = (bookingId, userId) => {
  const timestamp = Date.now();
  return `${bookingId}-${userId}-${timestamp}`;
};

// ==================== FLIGHT CONFIRMATION ====================

export const FlightBookingConfirmation = ({
  flightData,
  reviewData,
  onConfirm,
  onCancel,
  onBack,
  seatMapData,
  selectedSeats = {},
  onSeatSelect,
  seatRequired = false,
  loadingSeatMap = false
}) => {
  const [bookingState, setBookingState] = useState(BOOKING_STATES.IDLE);
  const [showFareBreakdown, setShowFareBreakdown] = useState(false);
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [showSeatMap, setShowSeatMap] = useState(false);
  const [userConfirmed, setUserConfirmed] = useState(false);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [revalidatedFare, setRevalidatedFare] = useState(null);
  const idempotencyKeyRef = useRef(null);
  const processingRef = useRef(false);

  // Extract flight details from review response
  const tripInfo = reviewData?.data?.tripInfos?.[0] || {};
  const segments = tripInfo?.sI || [];
  const priceInfo = reviewData?.data?.totalPriceInfo?.totalFareDetail?.fC || {};
  const conditions = reviewData?.data?.conditions || {};
  const bookingId = reviewData?.booking_id;

  // Revalidate fare before final confirmation
  const revalidateFare = useCallback(async () => {
    if (!bookingId) return false;
    
    setIsRevalidating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tripjack/flights/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ price_ids: [flightData?.priceId] })
      });
      
      const data = await response.json();
      if (data.success) {
        const newFare = data.data?.totalPriceInfo?.totalFareDetail?.fC?.TF;
        const oldFare = priceInfo?.TF;
        
        if (newFare && oldFare && Math.abs(newFare - oldFare) > 1) {
          setRevalidatedFare(newFare);
          toast.warning(`Fare updated: ₹${oldFare.toLocaleString()} → ₹${newFare.toLocaleString()}`);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Fare revalidation failed:', error);
      return false;
    } finally {
      setIsRevalidating(false);
    }
  }, [bookingId, flightData?.priceId, priceInfo?.TF]);

  // Handle final booking confirmation with duplicate protection
  const handleConfirmBooking = async () => {
    // Prevent double-clicks and concurrent requests
    if (processingRef.current || bookingState === BOOKING_STATES.PROCESSING) {
      return;
    }

    if (!userConfirmed) {
      toast.error('Please confirm you have read and agree to the booking conditions');
      return;
    }

    processingRef.current = true;
    setBookingState(BOOKING_STATES.CONFIRMING);

    // Generate idempotency key for this booking attempt
    const userId = localStorage.getItem('userId') || 'guest';
    idempotencyKeyRef.current = generateIdempotencyKey(bookingId, userId);

    // Step 1: Revalidate fare
    const isValid = await revalidateFare();
    if (!isValid) {
      toast.error('Unable to validate current fare. Please try again.');
      setBookingState(BOOKING_STATES.ERROR);
      processingRef.current = false;
      return;
    }

    setBookingState(BOOKING_STATES.PROCESSING);

    // Step 2: Call parent confirm handler with idempotency key
    try {
      await onConfirm({
        bookingId,
        idempotencyKey: idempotencyKeyRef.current,
        revalidatedFare: revalidatedFare || priceInfo?.TF
      });
      setBookingState(BOOKING_STATES.SUCCESS);
    } catch (error) {
      setBookingState(BOOKING_STATES.ERROR);
      toast.error(error.message || 'Booking failed. Please try again.');
    } finally {
      processingRef.current = false;
    }
  };

  const displayFare = revalidatedFare || priceInfo?.TF || 0;
  const baseFare = priceInfo?.BF || 0;
  const taxes = priceInfo?.TAF || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0a0a1a] rounded-2xl border border-white/10 overflow-hidden shadow-xl"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-white/20 rounded-xl">
            <Plane className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Confirm Flight Booking</h2>
            <p className="text-white/80 text-sm">Review all details before confirming</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6 bg-[#0a0a1a]">
        {/* Fare Validation Notice */}
        <div className="bg-amber-500/20 border border-amber-500/40 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 text-sm font-semibold">Important Notice</p>
              <p className="text-amber-200 text-xs mt-1">
                The fare shown has been validated but may be subject to availability until booking is confirmed. 
                Final fare will be revalidated before payment.
              </p>
            </div>
          </div>
        </div>

        {/* Flight Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Plane className="w-5 h-5 text-indigo-400" />
            Flight Details
          </h3>

          {segments.map((segment, idx) => (
            <div key={idx} className="bg-[#121228] rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-800">
                      {segment?.fD?.aI?.code || 'XX'}
                    </span>
                  </div>
                  <div>
                    <p className="text-white font-semibold">
                      {segment?.fD?.aI?.name || 'Airline'}
                    </p>
                    <p className="text-gray-300 text-sm">
                      {segment?.fD?.fN || 'Flight'} • {segment?.fD?.eT || 'Aircraft'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-gray-300 text-sm">Duration</p>
                  <p className="text-white font-semibold">{segment?.duration ? `${Math.floor(segment.duration / 60)}h ${segment.duration % 60}m` : 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">
                    {segment?.da?.code || 'DEP'}
                  </p>
                  <p className="text-gray-300 text-sm">{segment?.da?.city || 'City'}</p>
                  <p className="text-white text-sm mt-1 font-medium">
                    {segment?.dt ? new Date(segment.dt).toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'Time'}
                  </p>
                  <p className="text-gray-300 text-xs">
                    {segment?.dt ? new Date(segment.dt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    }) : 'Date'}
                  </p>
                </div>

                <div className="flex-1 px-4">
                  <div className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-indigo-500" />
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500" />
                    <Plane className="w-4 h-4 text-indigo-400 mx-2" />
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-500" />
                    <div className="w-2 h-2 rounded-full bg-purple-500" />
                  </div>
                  <p className="text-center text-gray-300 text-xs mt-1">
                    {segment?.stops === 0 ? 'Non-stop' : `${segment?.stops} stop(s)`}
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-2xl font-bold text-white">
                    {segment?.aa?.code || 'ARR'}
                  </p>
                  <p className="text-gray-300 text-sm">{segment?.aa?.city || 'City'}</p>
                  <p className="text-white text-sm mt-1 font-medium">
                    {segment?.at ? new Date(segment.at).toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'Time'}
                  </p>
                  <p className="text-gray-300 text-xs">
                    {segment?.at ? new Date(segment.at).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    }) : 'Date'}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Baggage Info */}
        {tripInfo?.totalPriceList?.[0]?.fd?.ADULT?.bI && (
          <div className="bg-[#121228] rounded-xl p-4 border border-white/10">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Luggage className="w-4 h-4 text-indigo-400" />
              Baggage Allowance
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-300">Cabin Baggage</p>
                <p className="text-white font-medium">
                  {tripInfo.totalPriceList[0].fd.ADULT.bI?.cB || '7 kg'}
                </p>
              </div>
              <div>
                <p className="text-gray-300">Check-in Baggage</p>
                <p className="text-white font-medium">
                  {tripInfo.totalPriceList[0].fd.ADULT.bI?.iB || '15 kg'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Seat Selection */}
        {(seatMapData?.hasSeatMap || seatRequired || loadingSeatMap) && (
          <div className="bg-[#121228] rounded-xl overflow-hidden border border-white/10">
            <button
              onClick={() => setShowSeatMap(!showSeatMap)}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="5" width="4" height="14" rx="1" />
                  <rect x="10" y="5" width="4" height="14" rx="1" />
                  <rect x="17" y="5" width="4" height="14" rx="1" />
                </svg>
                <span className="text-white font-semibold">
                  Seat Selection {seatRequired && <span className="text-red-400 text-xs ml-1">(Required)</span>}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {loadingSeatMap ? (
                  <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />
                ) : Object.keys(selectedSeats).length > 0 ? (
                  <span className="text-green-400 text-sm">
                    {Object.values(selectedSeats).map(s => s.seatNo).join(', ')}
                  </span>
                ) : (
                  <span className="text-gray-400 text-sm">Choose seat</span>
                )}
                {showSeatMap ? (
                  <ChevronUp className="w-5 h-5 text-gray-300" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-300" />
                )}
              </div>
            </button>

            <AnimatePresence>
              {showSeatMap && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-white/10"
                >
                  <div className="p-4 bg-[#0a0a1a]">
                    {loadingSeatMap ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
                        <span className="ml-3 text-gray-400">Loading seat map...</span>
                      </div>
                    ) : seatMapData?.segments?.length > 0 ? (
                      <div className="space-y-4">
                        {/* Seat Legend */}
                        <div className="flex items-center justify-center gap-4 text-xs">
                          <div className="flex items-center gap-1">
                            <div className="w-4 h-4 rounded bg-green-500/80" />
                            <span className="text-gray-300">Available</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-4 h-4 rounded bg-indigo-500" />
                            <span className="text-gray-300">Selected</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-4 h-4 rounded bg-gray-600" />
                            <span className="text-gray-300">Booked</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-4 h-4 rounded bg-amber-500/60" />
                            <span className="text-gray-300">Extra legroom</span>
                          </div>
                        </div>

                        {/* Seat Grid */}
                        {seatMapData.segments.map((segment, segIdx) => (
                          <div key={segIdx} className="bg-[#121228] rounded-lg p-4">
                            <p className="text-sm text-gray-400 mb-3">
                              {segment.availableCount} seats available
                            </p>
                            <div className="flex flex-wrap gap-1.5 justify-center max-h-48 overflow-y-auto">
                              {segment.seats.map((seat, seatIdx) => {
                                const isSelected = Object.values(selectedSeats).some(s => s.seatNo === seat.seatNo);
                                const isAvailable = seat.isAvailable;
                                
                                return (
                                  <button
                                    key={seatIdx}
                                    disabled={!isAvailable}
                                    onClick={() => {
                                      if (isAvailable && onSeatSelect) {
                                        onSeatSelect(0, isSelected ? null : seat);
                                      }
                                    }}
                                    className={`
                                      w-8 h-8 rounded text-xs font-medium transition-all
                                      ${!isAvailable 
                                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                                        : isSelected 
                                          ? 'bg-indigo-500 text-white ring-2 ring-indigo-400' 
                                          : seat.isLegroom 
                                            ? 'bg-amber-500/60 text-white hover:bg-amber-500/80' 
                                            : 'bg-green-500/80 text-white hover:bg-green-500'
                                      }
                                    `}
                                    title={`${seat.seatNo}${seat.isFree ? ' (Free)' : ` - ₹${seat.price}`}${seat.isWindow ? ' • Window' : ''}${seat.isAisle ? ' • Aisle' : ''}`}
                                  >
                                    {seat.seatNo}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}

                        {/* Selected Seat Info */}
                        {Object.values(selectedSeats).length > 0 && (
                          <div className="bg-indigo-500/10 rounded-lg p-3 text-center">
                            <p className="text-indigo-400">
                              Selected: {Object.values(selectedSeats).map(s => (
                                <span key={s.seatNo} className="font-semibold">
                                  {s.seatNo} {s.isFree ? '(Free)' : `(+₹${s.price})`}
                                </span>
                              ))}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-400">
                        <p>Seat selection is not available for this flight.</p>
                        <p className="text-sm mt-1">Seats will be assigned at check-in.</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Fare Breakdown */}
        <div className="bg-[#121228] rounded-xl overflow-hidden border border-white/10">
          <button
            onClick={() => setShowFareBreakdown(!showFareBreakdown)}
            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-400" />
              <span className="text-white font-semibold">Fare Breakdown</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-green-400">
                ₹{displayFare.toLocaleString()}
              </span>
              {showFareBreakdown ? (
                <ChevronUp className="w-5 h-5 text-gray-300" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-300" />
              )}
            </div>
          </button>

          <AnimatePresence>
            {showFareBreakdown && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-white/10"
              >
                <div className="p-4 space-y-2 bg-[#0a0a1a]">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-300">Base Fare</span>
                    <span className="text-white font-medium">₹{baseFare.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-300">Taxes & Fees</span>
                    <span className="text-white font-medium">₹{taxes.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 flex justify-between font-semibold">
                    <span className="text-white">Total</span>
                    <span className="text-green-400">₹{displayFare.toLocaleString()}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Booking Conditions */}
        <div className="bg-[#121228] rounded-xl overflow-hidden border border-white/10">
          <button
            onClick={() => setShowCancellationPolicy(!showCancellationPolicy)}
            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              <span className="text-white font-semibold">Cancellation & Booking Conditions</span>
            </div>
            {showCancellationPolicy ? (
              <ChevronUp className="w-5 h-5 text-gray-300" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-300" />
            )}
          </button>

          <AnimatePresence>
            {showCancellationPolicy && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-white/10"
              >
                <div className="p-4 space-y-3 bg-[#0a0a1a]">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-200 text-sm">
                      Cancellation charges may apply as per airline policy. 
                      Refund processing time: 5-7 business days.
                    </p>
                  </div>
                  {conditions?.isLcc && (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-200 text-sm">
                        This is a low-cost carrier. Additional fees may apply for meals, 
                        baggage, and seat selection.
                      </p>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-200 text-sm">
                      Please arrive at the airport at least 2 hours before departure.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Confirmation Checkbox */}
        <div className="bg-[#121228] rounded-xl p-4 border border-white/10">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={userConfirmed}
              onChange={(e) => setUserConfirmed(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-gray-500 bg-white/10 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-gray-200 text-sm">
              I confirm that I have read and agree to the fare rules, cancellation policy, 
              and booking conditions. I understand that the final fare will be validated 
              before payment and may vary based on availability.
            </span>
          </label>
        </div>

        {/* Seat Selection Warning */}
        {seatRequired && Object.keys(selectedSeats).length === 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <p className="text-amber-200 text-sm">
              Seat selection is mandatory for this flight. Please select a seat to continue.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 pt-2">
          <button
            onClick={onBack}
            disabled={bookingState === BOOKING_STATES.PROCESSING}
            className="flex-1 py-4 px-6 rounded-xl border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            Back
          </button>
          <button
            onClick={handleConfirmBooking}
            disabled={
              !userConfirmed || 
              bookingState === BOOKING_STATES.PROCESSING || 
              isRevalidating ||
              (seatRequired && Object.keys(selectedSeats).length === 0)
            }
            className={`flex-1 py-4 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
              userConfirmed && bookingState !== BOOKING_STATES.PROCESSING && (!seatRequired || Object.keys(selectedSeats).length > 0)
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500 shadow-lg shadow-green-500/20'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            {bookingState === BOOKING_STATES.PROCESSING ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : isRevalidating ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Validating Fare...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Confirm & Pay ₹{displayFare.toLocaleString()}
              </>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ==================== HOTEL CONFIRMATION ====================

export const HotelBookingConfirmation = ({
  hotelData,
  reviewData,
  pricingData,
  onConfirm,
  onCancel,
  onBack
}) => {
  const [bookingState, setBookingState] = useState(BOOKING_STATES.IDLE);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(true);
  const [showHotelPolicies, setShowHotelPolicies] = useState(false);
  const [showGuestDetails, setShowGuestDetails] = useState(true);
  const [userConfirmed, setUserConfirmed] = useState(false);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [guests, setGuests] = useState([]);
  const [guestsValid, setGuestsValid] = useState(false);
  const idempotencyKeyRef = useRef(null);
  const processingRef = useRef(false);

  // Extract data
  const bookingId = reviewData?.booking_id || reviewData?.data?.bookingId;
  const hotelInfo = reviewData?.data?.hotelInfo || hotelData;
  const roomInfo = reviewData?.data?.roomInfo || pricingData?.options?.[0]?.roomInfo || [];
  const pricing = pricingData?.options?.[0]?.pricing || reviewData?.data?.pricing || {};
  const cancellationPolicy = pricingData?.options?.[0]?.cancellationPolicy || reviewData?.data?.cancellationPolicy || {};
  const mealBasis = pricingData?.options?.[0]?.mealBasis || 'Room Only';
  const inclusions = pricingData?.options?.[0]?.inclusions || [];
  
  // Check if PAN is required from review response
  const compliance = reviewData?.data?.compliance || pricingData?.options?.[0]?.compliance || {};
  const panRequired = compliance?.panRequired || reviewData?.data?.ipr || false;
  const passportRequired = compliance?.passportRequired || reviewData?.data?.ipm || false;

  const displayPrice = pricing?.totalPrice || 0;
  const basePrice = pricing?.basePrice || 0;
  const taxes = pricing?.taxes || 0;

  // Handle guest validation change
  const handleGuestValidation = useCallback((isValid, guestData) => {
    setGuestsValid(isValid);
  }, []);

  // Revalidate before booking
  const revalidateSession = useCallback(async () => {
    setIsRevalidating(true);
    // Session validation would happen here if needed
    // For TripJack, the review hash ensures session validity
    await new Promise(resolve => setTimeout(resolve, 500));
    setIsRevalidating(false);
    return true;
  }, []);

  // Handle final booking confirmation
  const handleConfirmBooking = async () => {
    if (processingRef.current || bookingState === BOOKING_STATES.PROCESSING) {
      return;
    }

    if (!userConfirmed) {
      toast.error('Please confirm you have read and agree to the booking conditions');
      return;
    }

    // Validate guests have been entered
    if (guests.length === 0 || !guestsValid) {
      toast.error('Please complete all guest details' + (panRequired ? ', including PAN' : ''));
      setShowGuestDetails(true);
      return;
    }

    processingRef.current = true;
    setBookingState(BOOKING_STATES.CONFIRMING);

    const userId = localStorage.getItem('userId') || 'guest';
    idempotencyKeyRef.current = generateIdempotencyKey(bookingId, userId);

    // Validate session
    const isValid = await revalidateSession();
    if (!isValid) {
      toast.error('Session expired. Please search again.');
      setBookingState(BOOKING_STATES.ERROR);
      processingRef.current = false;
      return;
    }

    setBookingState(BOOKING_STATES.PROCESSING);

    try {
      // Pass guest data including PAN to the confirmation handler
      await onConfirm({
        bookingId,
        idempotencyKey: idempotencyKeyRef.current,
        amount: displayPrice,
        guests: guests.map(g => ({
          title: g.title,
          firstName: g.firstName,
          lastName: g.lastName,
          pan: panRequired ? g.pan : undefined, // Only include PAN if required
          isLead: g.isLead
        }))
      });
      setBookingState(BOOKING_STATES.SUCCESS);
    } catch (error) {
      setBookingState(BOOKING_STATES.ERROR);
      // Handle PAN-specific errors
      if (error.message?.toLowerCase().includes('pan')) {
        toast.error('Please enter a valid PAN number');
      } else {
        toast.error(error.message || 'Booking failed. Please try again.');
      }
    } finally {
      processingRef.current = false;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0a0a1a] rounded-2xl border border-white/10 overflow-hidden shadow-xl"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-white/20 rounded-xl">
            <Hotel className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Confirm Hotel Booking</h2>
            <p className="text-white/80 text-sm">Review all details before confirming</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6 bg-[#0a0a1a]">
        {/* Review Session Notice */}
        <div className="bg-blue-500/20 border border-blue-500/40 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-300 text-sm font-semibold">Booking Session Active</p>
              <p className="text-blue-200 text-xs mt-1">
                Your room selection is being held. Please complete your booking soon 
                to secure this rate. Prices may change if the session expires.
              </p>
            </div>
          </div>
        </div>

        {/* Hotel Details */}
        <div className="bg-[#121228] rounded-xl p-4 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Hotel className="w-5 h-5 text-emerald-400" />
            Hotel Details
          </h3>

          <div className="space-y-3">
            <div>
              <p className="text-xl font-bold text-white">
                {hotelInfo?.name || hotelData?.name || 'Hotel'}
              </p>
              {hotelInfo?.ad?.adr && (
                <p className="text-gray-300 text-sm flex items-center gap-1 mt-1">
                  <MapPin className="w-4 h-4" />
                  {hotelInfo.ad.adr}, {hotelInfo?.ad?.city?.name || ''}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-white/10">
              <div>
                <p className="text-gray-300 text-sm">Check-in</p>
                <p className="text-white font-semibold flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-emerald-400" />
                  {hotelData?.checkinDate || 'Date'}
                </p>
                {hotelInfo?.cin && (
                  <p className="text-gray-300 text-xs">From {hotelInfo.cin}</p>
                )}
              </div>
              <div>
                <p className="text-gray-300 text-sm">Check-out</p>
                <p className="text-white font-semibold flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-emerald-400" />
                  {hotelData?.checkoutDate || 'Date'}
                </p>
                {hotelInfo?.cout && (
                  <p className="text-gray-300 text-xs">Until {hotelInfo.cout}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Room Details */}
        <div className="bg-[#121228] rounded-xl p-4 border border-white/10">
          <h4 className="text-white font-semibold mb-3">Room Details</h4>
          {(Array.isArray(roomInfo) ? roomInfo : [roomInfo]).map((room, idx) => (
            <div key={idx} className="flex items-center justify-between py-2">
              <div>
                <p className="text-white font-medium">{room?.name || room?.rc || 'Room'}</p>
                <p className="text-gray-300 text-sm">
                  {room?.adults || 2} {(room?.adults || 2) === 1 ? 'Adult' : 'Adults'} {room?.children > 0 && `• ${room.children} Children`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-300" />
                <span className="text-gray-300 text-sm">
                  {hotelData?.rooms || 1} Room(s)
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Guest Details Section */}
        <div className="bg-[#121228] rounded-xl overflow-hidden border border-white/10">
          <button
            onClick={() => setShowGuestDetails(!showGuestDetails)}
            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            data-testid="guest-details-toggle"
          >
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-400" />
              <span className="text-white font-semibold">
                Guest Details
                {panRequired && <span className="text-red-400 text-xs ml-2">(PAN Required)</span>}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {guestsValid ? (
                <span className="text-green-400 text-sm flex items-center gap-1">
                  <Check className="w-4 h-4" /> Complete
                </span>
              ) : (
                <span className="text-amber-400 text-sm">Enter details</span>
              )}
              {showGuestDetails ? (
                <ChevronUp className="w-5 h-5 text-gray-300" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-300" />
              )}
            </div>
          </button>

          <AnimatePresence>
            {showGuestDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-white/10"
              >
                <div className="p-4 bg-[#0a0a1a]">
                  <HotelGuestForm
                    guests={guests}
                    onGuestsChange={setGuests}
                    roomInfo={Array.isArray(roomInfo) ? roomInfo : [roomInfo]}
                    panRequired={panRequired}
                    passportRequired={passportRequired}
                    onValidationChange={handleGuestValidation}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Meal & Inclusions */}
        <div className="bg-[#121228] rounded-xl p-4 border border-white/10">
          <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Coffee className="w-4 h-4 text-amber-400" />
            Meal Plan & Inclusions
          </h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span className="text-white font-medium">{mealBasis}</span>
            </div>
            {inclusions.map((inc, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <span className="text-gray-200 text-sm">{inc}</span>
              </div>
            ))}
            {inclusions.length === 0 && mealBasis === 'Room Only' && (
              <p className="text-gray-300 text-sm">No meals included. Meals available at additional cost.</p>
            )}
          </div>
        </div>

        {/* Cancellation Policy - Always Visible */}
        <div className="bg-[#121228] rounded-xl overflow-hidden border border-white/10">
          <button
            onClick={() => setShowCancellationPolicy(!showCancellationPolicy)}
            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              <span className="text-white font-semibold">Cancellation Policy</span>
            </div>
            {showCancellationPolicy ? (
              <ChevronUp className="w-5 h-5 text-gray-300" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-300" />
            )}
          </button>

          <AnimatePresence>
            {showCancellationPolicy && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-white/10"
              >
                <div className="p-4 space-y-3 bg-[#0a0a1a]">
                  {cancellationPolicy?.ifra ? (
                    <div className="flex items-center gap-2 text-green-400">
                      <Check className="w-5 h-5" />
                      <span className="font-semibold">Free Cancellation Available</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-400">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-semibold">Non-Refundable Booking</span>
                    </div>
                  )}

                  {cancellationPolicy?.pd?.map((policy, idx) => (
                    <div key={idx} className="bg-[#121228] rounded-lg p-3 border border-white/10">
                      <p className="text-gray-200 text-sm">{policy.fcs || 'Cancellation terms apply'}</p>
                      {policy.am > 0 && (
                        <p className="text-amber-400 text-sm mt-1 font-medium">
                          Cancellation charge: ₹{policy.am.toLocaleString()}
                        </p>
                      )}
                    </div>
                  ))}

                  {(!cancellationPolicy?.pd || cancellationPolicy.pd.length === 0) && (
                    <p className="text-gray-300 text-sm">
                      Please contact the hotel for cancellation terms.
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Price Breakdown */}
        <div className="bg-[#121228] rounded-xl overflow-hidden border border-white/10">
          <button
            onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-400" />
              <span className="text-white font-semibold">Price Breakdown</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-green-400">
                ₹{displayPrice.toLocaleString()}
              </span>
              {showPriceBreakdown ? (
                <ChevronUp className="w-5 h-5 text-gray-300" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-300" />
              )}
            </div>
          </button>

          <AnimatePresence>
            {showPriceBreakdown && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-white/10"
              >
                <div className="p-4 space-y-2 bg-[#0a0a1a]">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-300">Room Charges</span>
                    <span className="text-white font-medium">₹{basePrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-300">Taxes & Fees</span>
                    <span className="text-white font-medium">₹{taxes.toLocaleString()}</span>
                  </div>
                  {pricing?.mf > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300">Service Fee</span>
                      <span className="text-white font-medium">₹{pricing.mf.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-2 flex justify-between font-semibold">
                    <span className="text-white">Total</span>
                    <span className="text-green-400">₹{displayPrice.toLocaleString()}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Hotel Policies */}
        <div className="bg-[#121228] rounded-xl overflow-hidden border border-white/10">
          <button
            onClick={() => setShowHotelPolicies(!showHotelPolicies)}
            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-400" />
              <span className="text-white font-semibold">Important Hotel Policies</span>
            </div>
            {showHotelPolicies ? (
              <ChevronUp className="w-5 h-5 text-gray-300" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-300" />
            )}
          </button>

          <AnimatePresence>
            {showHotelPolicies && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-white/10"
              >
                <div className="p-4 space-y-3 text-sm bg-[#0a0a1a]">
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-200">
                      Check-in: {hotelInfo?.cin || '14:00'} | Check-out: {hotelInfo?.cout || '12:00'}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-200">
                      Valid ID proof required at check-in for all guests
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CreditCard className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-200">
                      Early check-in or late check-out may be available at additional cost
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Guest Details Warning */}
        {!guestsValid && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <p className="text-amber-200 text-sm">
              Please complete all guest details{panRequired ? ', including PAN' : ''} to proceed with booking.
            </p>
          </div>
        )}

        {/* Confirmation Checkbox */}
        <div className="bg-[#121228] rounded-xl p-4 border border-white/10">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={userConfirmed}
              onChange={(e) => setUserConfirmed(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-gray-500 bg-white/10 text-emerald-600 focus:ring-emerald-500"
              data-testid="booking-confirmation-checkbox"
            />
            <span className="text-gray-200 text-sm">
              I confirm that I have read and agree to the hotel policies, cancellation policy, 
              and booking conditions. I understand that the booking is subject to availability 
              and the final price may include additional taxes as per local regulations.
            </span>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-2">
          <button
            onClick={onBack}
            disabled={bookingState === BOOKING_STATES.PROCESSING}
            className="flex-1 py-4 px-6 rounded-xl border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors disabled:opacity-50"
            data-testid="booking-back-btn"
          >
            Back
          </button>
          <button
            onClick={handleConfirmBooking}
            disabled={!userConfirmed || !guestsValid || bookingState === BOOKING_STATES.PROCESSING || isRevalidating}
            className={`flex-1 py-4 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
              userConfirmed && guestsValid && bookingState !== BOOKING_STATES.PROCESSING
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/20'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="booking-confirm-btn"
          >
            {bookingState === BOOKING_STATES.PROCESSING ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : isRevalidating ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Validating...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Confirm & Pay ₹{displayPrice.toLocaleString()}
              </>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ==================== CANCELLATION CONFIRMATION ====================

export const CancellationConfirmation = ({
  bookingDetails,
  bookingType, // 'flight' or 'hotel'
  onConfirm,
  onCancel
}) => {
  const [userConfirmed, setUserConfirmed] = useState(false);
  const [processing, setProcessing] = useState(false);
  const processingRef = useRef(false);

  const handleCancellation = async () => {
    if (processingRef.current || processing) return;
    
    if (!userConfirmed) {
      toast.error('Please confirm you understand the cancellation consequences');
      return;
    }

    processingRef.current = true;
    setProcessing(true);

    try {
      await onConfirm(bookingDetails.bookingId);
    } catch (error) {
      toast.error(error.message || 'Cancellation failed');
    } finally {
      setProcessing(false);
      processingRef.current = false;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
    >
      <div className="bg-[var(--bg-secondary)] rounded-2xl border border-white/10 max-w-md w-full overflow-hidden">
        <div className="bg-red-600 p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-white" />
            <h3 className="text-lg font-bold text-white">Cancel Booking</h3>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <p className="text-red-200 text-sm">
              <strong>Warning:</strong> This action cannot be undone. 
              Cancellation charges may apply based on the booking policy.
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-gray-400 text-sm">Booking Reference:</p>
            <p className="text-white font-mono">{bookingDetails?.bookingId}</p>
          </div>

          {bookingDetails?.cancellationCharges > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <p className="text-amber-200 text-sm">
                Cancellation charge: <strong>₹{bookingDetails.cancellationCharges.toLocaleString()}</strong>
              </p>
              <p className="text-amber-200/70 text-xs mt-1">
                Refund amount: ₹{(bookingDetails.totalAmount - bookingDetails.cancellationCharges).toLocaleString()}
              </p>
            </div>
          )}

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={userConfirmed}
              onChange={(e) => setUserConfirmed(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-gray-600 bg-white/10 text-red-600 focus:ring-red-500"
            />
            <span className="text-gray-300 text-sm">
              I understand that this booking will be cancelled and the applicable 
              cancellation charges will be deducted from my refund.
            </span>
          </label>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onCancel}
              disabled={processing}
              className="flex-1 py-3 px-4 rounded-xl border border-white/20 text-white font-medium hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              Keep Booking
            </button>
            <button
              onClick={handleCancellation}
              disabled={!userConfirmed || processing}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                userConfirmed && !processing
                  ? 'bg-red-600 text-white hover:bg-red-500'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <Ban className="w-5 h-5" />
                  Cancel Booking
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default {
  FlightBookingConfirmation,
  HotelBookingConfirmation,
  CancellationConfirmation
};
